import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, ExternalLink, Download, MapPin, Loader2, Linkedin, Instagram, Facebook, Twitter, Globe, Sparkles, ShieldCheck, Info, RefreshCw } from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Public digital business card — accessed via NVN or user_id */
export default function PublicCard() {
  const { key } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/cards/${key}`).then(r => setCard(r.data)).catch(() => setCard(null)).finally(() => setLoading(false));
  }, [key]);

  if (loading) return <div className="p-12 text-center text-stone-500"><Loader2 className="inline animate-spin mr-2" /> Loading card…</div>;
  if (!card) return (
    <div className="max-w-md mx-auto p-12 text-center">
      <div className="text-lg font-bold mb-2">Card not found</div>
      <p className="text-sm text-stone-500 mb-4">This NVN or user hasn&apos;t published a card yet.</p>
      <Link to="/"><Button variant="outline" className="rounded-full">Go home</Button></Link>
    </div>
  );

  const c = card.card || {};
  const socials = [
    { key: "linkedin_url", label: "LinkedIn", Icon: Linkedin, color: "text-[#0A66C2]" },
    { key: "instagram_url", label: "Instagram", Icon: Instagram, color: "text-pink-600" },
    { key: "facebook_url", label: "Facebook", Icon: Facebook, color: "text-blue-600" },
    { key: "twitter_url", label: "Twitter", Icon: Twitter, color: "text-stone-900" },
    { key: "website_url", label: "Website", Icon: Globe, color: "text-emerald-700" },
  ].filter(s => c[s.key]);

  return (
    <div className="max-w-xl mx-auto px-5 py-8">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-lg" data-testid="public-card">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#2D5A27] to-[#1a3e17] text-white p-6 md:p-7 relative">
          <div className="flex items-center gap-4">
            {card.picture ? (
              <img loading="lazy" src={card.picture} alt={card.name} className="w-20 h-20 rounded-full border-4 border-white/20 object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-white/10 grid place-items-center text-2xl font-black">{card.name?.[0] || "?"}</div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="heading text-2xl md:text-3xl font-extrabold truncate">{card.name}</h1>
              {card.business_name && <div className="text-emerald-200 text-sm truncate">{card.business_name}</div>}
              {card.occupation && <div className="text-emerald-100/80 text-xs">{card.occupation}</div>}
            </div>
          </div>
          {(card.nvn || card.trust) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {card.nvn && (
                <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-3 py-1 text-xs font-mono font-bold">
                  {card.is_fancy_nvn && <Sparkles size={11} className="text-amber-300" />}
                  NVN {card.nvn}
                </div>
              )}
              {card.trust && <TrustBadge trust={card.trust} cardKey={key} onUpdate={fresh => setCard(c => ({ ...c, trust: fresh }))} />}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-5 md:p-6 space-y-4">
          {c.tagline && <div className="text-lg font-bold text-stone-900">{c.tagline}</div>}
          {card.bio && <div className="text-sm text-stone-600 leading-relaxed">{card.bio}</div>}

          {(card.city || c.address) && (
            <div className="flex items-start gap-2 text-sm text-stone-700">
              <MapPin size={14} className="mt-0.5 text-stone-400 shrink-0" />
              <span>{c.address || card.city}</span>
            </div>
          )}

          {/* Primary CTAs */}
          <div className="flex flex-wrap gap-2 pt-2">
            {card.mobile && (
              <a href={`tel:${card.mobile}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white text-sm font-bold px-4 py-2 transition-colors" data-testid="card-call">
                <Phone size={14} /> Call
              </a>
            )}
            {c.whatsapp_number && (
              <a href={`https://wa.me/${c.whatsapp_number.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 transition-colors" data-testid="card-whatsapp">
                <MessageCircle size={14} /> WhatsApp
              </a>
            )}
            <a href={`${API}/cards/${key}.vcf`} download className="inline-flex items-center gap-1.5 rounded-full border-2 border-stone-300 hover:border-stone-900 text-sm font-bold px-4 py-2 transition-colors" data-testid="card-save-contact">
              <Download size={14} /> Save contact
            </a>
            <GoogleWalletButton cardKey={key} />
          </div>

          {/* Socials */}
          {socials.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-3 border-t border-stone-100">
              {socials.map(s => (
                <a key={s.key} href={c[s.key]} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 rounded-full border border-stone-200 hover:border-stone-400 text-sm font-bold px-3 py-1.5 transition-colors ${s.color}`} data-testid={`card-social-${s.key}`}>
                  <s.Icon size={14} /> {s.label}
                </a>
              ))}
            </div>
          )}

          {/* External directories */}
          {(c.justdial_url || c.google_business_url) && (
            <div className="flex flex-wrap gap-2">
              {c.justdial_url && <a href={c.justdial_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-stone-500 hover:text-[#E05E36] inline-flex items-center gap-1" data-testid="card-justdial">JustDial <ExternalLink size={10} /></a>}
              {c.google_business_url && <a href={c.google_business_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-stone-500 hover:text-[#E05E36] inline-flex items-center gap-1" data-testid="card-google">Google <ExternalLink size={10} /></a>}
              {c.catalogue_url && <a href={c.catalogue_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-stone-500 hover:text-[#E05E36] inline-flex items-center gap-1" data-testid="card-catalogue">Catalogue <ExternalLink size={10} /></a>}
            </div>
          )}

          {/* Listings */}
          {card.listings?.length > 0 && (
            <div className="pt-3 border-t border-stone-100">
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">Listings ({card.listings.length})</div>
              <div className="grid grid-cols-2 gap-2">
                {card.listings.map(l => (
                  <Link key={l.listing_id} to={`/listing/${l.listing_id}`} className="border border-stone-200 rounded-xl p-2.5 hover:border-[#E05E36] transition-colors" data-testid={`card-listing-${l.listing_id}`}>
                    <div className="font-bold text-xs text-stone-900 truncate">{l.title}</div>
                    <div className="text-[10px] text-stone-500 truncate">{l.category}</div>
                    {l.price > 0 && <div className="text-xs font-bold text-[#E05E36] mt-0.5">₹{l.price}</div>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-stone-100 px-5 py-3 text-[10px] text-stone-400 text-center">
          Powered by <Link to="/" className="text-[#2D5A27] font-bold hover:text-[#E05E36]">Find2Near</Link>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Compact trust badge for the card header. Colored by band; expands into a details tooltip
 * on hover/tap so viewers understand what the score actually measures.
 */
function TrustBadge({ trust, cardKey, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const band = trust?.band || { label: "New", color: "stone" };
  // Static maps so Tailwind's JIT scanner picks these classes up at build time.
  const chipColorMap = {
    emerald: "bg-emerald-400/20 text-emerald-100 border-emerald-300/40",
    green:   "bg-green-400/20   text-green-100   border-green-300/40",
    amber:   "bg-amber-400/20   text-amber-100   border-amber-300/40",
    orange:  "bg-orange-400/20  text-orange-100  border-orange-300/40",
    stone:   "bg-white/10       text-white/80    border-white/20",
  };
  const scoreColorMap = {
    emerald: "bg-emerald-100 text-emerald-800",
    green:   "bg-green-100   text-green-800",
    amber:   "bg-amber-100   text-amber-800",
    orange:  "bg-orange-100  text-orange-800",
    stone:   "bg-stone-100   text-stone-800",
  };
  const chip = chipColorMap[band.color] || chipColorMap.stone;
  const scoreChip = scoreColorMap[band.color] || scoreColorMap.stone;
  // Safe-defaults so a missing breakdown key never crashes the tooltip.
  const br = trust.breakdown || {};
  const sett = br.settlement || {}, ot = br.on_time || {}, vol = br.volume || {}, age = br.account_age || {}, kyc = br.kyc || {};

  return (
    <div className="relative" data-testid="trust-badge">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${chip} hover:brightness-110 transition-all`}
        data-testid="trust-badge-chip"
      >
        <ShieldCheck size={12} />
        Trust · {trust.score}
        <span className="opacity-70">· {band.label}</span>
        <Info size={10} className="opacity-60" />
      </button>
      {open && (
        <div
          className="absolute z-30 top-full left-0 mt-2 w-72 bg-white text-stone-900 rounded-2xl shadow-2xl border border-stone-200 p-4 text-xs"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          data-testid="trust-badge-tooltip"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-black text-sm">Payment reliability</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (refreshing || !cardKey) return;
                  setRefreshing(true);
                  try {
                    const r = await api.post(`/cards/${cardKey}/trust/refresh`);
                    onUpdate?.(r.data.trust);
                  } catch { /* silent — tooltip stays open */ }
                  finally { setRefreshing(false); }
                }}
                className="text-stone-400 hover:text-[#E05E36] disabled:opacity-50 transition-colors"
                disabled={refreshing}
                title="Recompute score from latest ledger activity"
                data-testid="trust-refresh-btn"
              >
                <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              </button>
              <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-black ${scoreChip}`}>
                {trust.score} / 100
              </div>
            </div>
          </div>
          <div className="text-stone-500 mb-3">
            Based on {trust.sample_size ?? 0} peer bill{(trust.sample_size ?? 0) === 1 ? "" : "s"}, account age & KYC.
          </div>
          <TrustRow  label="Settlement rate" v={sett.pts ?? 0} max={sett.max ?? 40}
            hint={sett.rate == null ? "No bills yet" : `${Math.round((sett.rate || 0) * 100)}% (${sett.settled ?? 0}/${sett.received ?? 0})`} />
          <TrustRow  label="On-time payment" v={ot.pts ?? 0}   max={ot.max ?? 20}
            hint={ot.rate == null ? "—" : `${Math.round((ot.rate || 0) * 100)}% within 15 days`} />
          <TrustRow  label="Transaction volume" v={vol.pts ?? 0} max={vol.max ?? 15}
            hint={`₹${((vol.total_paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} />
          <TrustRow  label="Account age"     v={age.pts ?? 0}   max={age.max ?? 15}
            hint={`${age.months ?? 0} months`} />
          <TrustRow  label="KYC verified"    v={kyc.pts ?? 0}   max={kyc.max ?? 10}
            hint={kyc.verified ? "Verified" : "Not verified"} />
          <div className="mt-3 pt-2 border-t border-stone-100 text-[10px] text-stone-400 leading-snug">
            Trust score is computed from Find2Near's peer-to-peer ledger — real behaviour, not self-reported.
          </div>
        </div>
      )}
    </div>
  );
}

function TrustRow({ label, v, max, hint }) {
  const pct = max > 0 ? Math.round((v / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="w-24 text-[11px] font-bold text-stone-700 shrink-0 truncate">{label}</div>
      <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full bg-emerald-500 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] font-mono text-stone-500 w-16 text-right truncate" title={hint}>{hint}</div>
    </div>
  );
}

/** "Add to Google Wallet" pill — fetches a signed save-JWT link from the backend.
 *  Only renders the actionable pill if the platform owner has configured Google Wallet
 *  credentials; otherwise it renders a subtle "coming soon" hint with a styled tooltip. */
function GoogleWalletButton({ cardKey }) {
  const [state, setState] = useState({ loading: true, save_url: null, configured: false });
  const [tipOpen, setTipOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/cards/${cardKey}/google-wallet`)
      .then(r => !cancelled && setState({ loading: false, ...r.data }))
      .catch(() => !cancelled && setState({ loading: false, save_url: null, configured: false }));
    return () => { cancelled = true; };
  }, [cardKey]);

  if (state.loading) return null;
  if (!state.configured || !state.save_url) {
    return (
      <span className="relative inline-block" onMouseEnter={() => setTipOpen(true)} onMouseLeave={() => setTipOpen(false)} onClick={() => setTipOpen(o => !o)}>
        <span
          className="inline-flex items-center gap-1.5 rounded-full border-2 border-dashed border-stone-300 text-stone-400 text-xs font-bold px-3 py-2 cursor-help select-none"
          data-testid="card-google-wallet-disabled"
        >
          <Sparkles size={12} /> Google Wallet · soon
        </span>
        {tipOpen && (
          <div
            className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-stone-900 text-stone-100 rounded-xl shadow-2xl border border-stone-700 p-3 text-[11px] leading-relaxed"
            role="tooltip"
            data-testid="google-wallet-tooltip"
          >
            <div className="font-black text-white mb-1">Not configured yet</div>
            <div className="text-stone-300">
              {state.reason || "Add your Google Wallet issuer credentials to backend/.env to enable this."}
            </div>
            {/* Speech-bubble arrow */}
            <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px w-2 h-2 rotate-45 bg-stone-900 border-r border-b border-stone-700" />
          </div>
        )}
      </span>
    );
  }
  return (
    <a
      href={state.save_url}
      target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full bg-black hover:bg-stone-800 text-white text-sm font-bold px-4 py-2 transition-colors"
      data-testid="card-google-wallet"
    >
      <Sparkles size={14} /> Add to Google Wallet
    </a>
  );
}
